import React, { memo, useMemo } from 'react'
import { Typography } from 'antd'
import { get, groupBy, isEmpty, keys, map, some, sortBy } from 'lodash'
import PropTypes from 'prop-types'

import { areSameValue } from '../../../core/utils'

import { findNodeByValue } from '../../../common/utils'

import {
  dishIngredientExplanationPropType,
  dishIngredientPropType,
} from '../../prop-types'
import {
  StyledIngredientExplanationContainer,
  StyledIngredientExplanationParagraph,
} from '../styled'

const INGREDIENT_TYPE_LABELS = {
  choiceIngredient: 'Choice ingredients',
  mainIngredient: 'Main ingredients',
  additionalIngredient: 'Additional ingredients',
  blacklistedIngredient: 'Blacklisted ingredients',
}

const DishDetailsIngredientExplanations = ({
  ingredientExplanations,
  dishIngredients,
  ingredientsTreeData,
}) => {
  const explanationsWithPath = useMemo(
    () =>
      sortBy(
        map(
          ingredientExplanations,
          ({ id, ingredientId, text, ingredientType }) => ({
            id,
            text,
            ingredientId,
            ingredientType,
            isRemoved: !some(dishIngredients, dishIngredient =>
              areSameValue(dishIngredient.ingredient?.id, ingredientId),
            ),
            ingredientPath: get(
              findNodeByValue(ingredientsTreeData, ingredientId),
              'namePath',
            ),
          }),
        ),
        'isRemoved',
      ),
    [ingredientExplanations, dishIngredients, ingredientsTreeData],
  )

  const explanationsByIngredientType = useMemo(
    () => groupBy(explanationsWithPath, 'ingredientType'),
    [explanationsWithPath],
  )

  if (isEmpty(explanationsByIngredientType)) {
    return null
  }

  return (
    <>
      <Typography.Paragraph strong>
        Ingredient explanations
      </Typography.Paragraph>
      {map(keys(explanationsByIngredientType), ingredientType => (
        <div key={ingredientType || 'other'}>
          <Typography.Text>
            {INGREDIENT_TYPE_LABELS[ingredientType] || ''}
          </Typography.Text>
          <StyledIngredientExplanationContainer>
            {map(
              explanationsByIngredientType[ingredientType],
              ({ id, text, ingredientPath, isRemoved }) => (
                <StyledIngredientExplanationParagraph
                  key={id}
                  isRemoved={isRemoved}
                >
                  {text} - {ingredientPath}
                </StyledIngredientExplanationParagraph>
              ),
            )}
          </StyledIngredientExplanationContainer>
        </div>
      ))}
    </>
  )
}

DishDetailsIngredientExplanations.propTypes = {
  ingredientExplanations: PropTypes.arrayOf(dishIngredientExplanationPropType)
    .isRequired,
  dishIngredients: PropTypes.arrayOf(dishIngredientPropType).isRequired,
  ingredientsTreeData: PropTypes.arrayOf(PropTypes.object).isRequired,
}

export default memo(DishDetailsIngredientExplanations)
